import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Play, 
  Square, 
  Download, 
  RefreshCw, 
  Trash2, 
  FileText, 
  Building2,
  Calendar,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Search,
  Terminal,
  Plus,
  ArrowLeft,
  User,
  Bot,
  MoreVertical,
  Menu,
  X,
  XCircle,
  Building,
  CheckCircle,
  RotateCcw,
  AlertCircle,
  Pencil,
  Globe,
  CreditCard,
  Receipt,
  MapPin,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Agents Sidebar Component (reusable for desktop and mobile)
function AgentsSidebar({ logins, selectedLogin, setSelectedLogin, onAddLogin, onEditLogin, onDeleteLogin, onClose, onSelectLogin }) {
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-zinc-300">Agents</h2>
        <Button variant="outline" size="sm" onClick={onAddLogin} data-testid="add-agent-btn" className="bg-zinc-800 border-zinc-700">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="space-y-2 flex-1 overflow-auto">
        {logins.map((login) => (
          <Card 
            key={login.id}
            className={`cursor-pointer transition-all ${
              selectedLogin?.id === login.id 
                ? 'bg-zinc-800 border-emerald-500/50' 
                : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
            }`}
            data-testid={`agent-card-${login.id}`}
          >
            <CardContent className="p-3">
              <div className="flex items-start justify-between">
                <div 
                  className="flex-1 min-w-0 cursor-pointer" 
                  onClick={() => {
                    setSelectedLogin(login);
                    if (onSelectLogin) onSelectLogin(login);
                    if (onClose) onClose();
                  }}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-sm truncate">{login.org_name || login.name}</h3>
                    {login.is_running && (
                      <Badge variant="outline" className="bg-blue-950/30 text-blue-400 border-blue-900/50 text-xs animate-pulse">
                        Running
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{login.email}</p>
                  {login.page_name && (
                    <p className="text-xs text-zinc-600 truncate flex items-center gap-1 mt-0.5">
                      <Globe className="w-3 h-3" />
                      {login.page_name}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-xs text-zinc-600">
                    <FileText className="w-3 h-3" />
                    <span>{login.bill_count?.toLocaleString() || 0} bills</span>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <MoreVertical className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem onClick={() => onEditLogin(login)} className="cursor-pointer">
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDeleteLogin(login.id, login.org_name || login.name)} className="text-red-400 cursor-pointer">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {logins.length === 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
            <CardContent className="p-6 text-center">
              <Bot className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
              <p className="text-sm text-zinc-500">No agents yet</p>
              <Button variant="outline" size="sm" onClick={onAddLogin} className="mt-3 bg-zinc-800 border-zinc-700">
                <Plus className="w-4 h-4 mr-1" />
                Add Agent
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function BotWorkspace() {
  const { botId } = useParams();
  const navigate = useNavigate();
  
  const [bot, setBot] = useState(null);
  const [logins, setLogins] = useState([]);
  const [selectedLogin, setSelectedLogin] = useState(null);
  const [activeDataTab, setActiveDataTab] = useState("bills"); // "bills", "payments", "reports", or "locations"
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reports, setReports] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationAccounts, setLocationAccounts] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [stats, setStats] = useState({ total_bills: 0, bills_with_s3: 0, total_payments: 0, payments_with_s3: 0, total_reports: 0, total_locations: 0, total_accounts: 0, top_vendors: [] });
  
  // Per-table settings
  const [billsHideDuplicates, setBillsHideDuplicates] = useState(true);
  const [paymentsHideDuplicates, setPaymentsHideDuplicates] = useState(true);
  const [reportsHideDuplicates, setReportsHideDuplicates] = useState(true);
  const [billsDuplicateCount, setBillsDuplicateCount] = useState(0);
  const [paymentsDuplicateCount, setPaymentsDuplicateCount] = useState(0);
  const [reportsDuplicateCount, setReportsDuplicateCount] = useState(0);
  
  // Row selection for deletion
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'bills'|'payments'|'reports', ids: [], all: false }
  
  // Table filters
  const [billFilters, setBillFilters] = useState({ vendor: "", billing_id: "" });
  const [paymentFilters, setPaymentFilters] = useState({ vendor: "" });
  const [reportFilters, setReportFilters] = useState({ report_name: "", status: "" });
  
  const [scraperStatus, setScraperStatus] = useState({
    is_running: false,
    current_page: 0,
    total_pages: 0,
    scraped_count: 0,
    status: "idle",
    can_resume: false,
    last_offset: 0,
    // Separate status for bills, payments, reports, and locations
    bills_status: "idle",
    bills_current_page: 0,
    bills_total_pages: 0,
    bills_scraped_count: 0,
    payments_status: "idle",
    payments_current_page: 0,
    payments_total_pages: 0,
    payments_scraped_count: 0,
    reports_status: "idle",
    reports_current_page: 0,
    reports_total_pages: 0,
    reports_scraped_count: 0,
    locations_status: "idle",
    locations_current_count: 0,
    locations_total_count: 0,
    locations_scraped_count: 0,
    active_scrape_type: null,
    bills_can_resume: false,
    payments_can_resume: false,
    reports_can_resume: false,
    locations_can_resume: false,
    // Per-scraper-type server tracking (fixes cross-server interference)
    bills_running_on_server: null,
    payments_running_on_server: null,
    reports_running_on_server: null,
    locations_running_on_server: null,
    bills_running_locally: false,
    payments_running_locally: false,
    reports_running_locally: false,
    locations_running_locally: false,
    // Status messages for better UX
    bills_status_message: "Idle",
    payments_status_message: "Idle",
    reports_status_message: "Idle",
    // Server tracking (legacy)
    running_on_server: null,
    this_server: null,
    playwright_running_locally: false
  });
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentTotalPages, setPaymentTotalPages] = useState(1);
  const [reportPage, setReportPage] = useState(1);
  const [reportTotalPages, setReportTotalPages] = useState(1);
  const [locationPage, setLocationPage] = useState(1);
  const [locationTotalPages, setLocationTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [showAddLogin, setShowAddLogin] = useState(false);
  const [showEditLogin, setShowEditLogin] = useState(false);
  const [editingLogin, setEditingLogin] = useState(null);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [newLogin, setNewLogin] = useState({ org_name: "", page_name: "", bills_url: "", payments_url: "", reports_url: "", locations_url: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  
  // Start page inputs for each scraper type
  const [billsStartPage, setBillsStartPage] = useState("");
  const [paymentsStartPage, setPaymentsStartPage] = useState("");
  const [reportsStartPage, setReportsStartPage] = useState("");

  const fetchBot = async () => {
    try {
      const response = await api.get(`/bots/${botId}`);
      setBot(response.data.bot);
    } catch (error) {
      toast.error("Failed to fetch bot");
      navigate('/');
    }
  };

  const fetchLogins = async (preserveSelection = true) => {
    try {
      const response = await api.get(`/bots/${botId}/logins`);
      const newLogins = response.data.logins;
      setLogins(newLogins);
      // Only set selectedLogin if we don't have one and there are logins available
      if (newLogins.length > 0 && !selectedLogin) {
        setSelectedLogin(newLogins[0]);
      }
      // Don't auto-update selectedLogin to prevent flickering - user explicitly selects agents
    } catch (error) {
      console.error("Failed to fetch logins:", error);
    }
  };

  const fetchBills = useCallback(async () => {
    if (!selectedLogin) return;
    try {
      const params = { page, limit: 20, hide_duplicates: billsHideDuplicates };
      if (search) params.search = search;
      if (billFilters.vendor) params.vendor = billFilters.vendor;
      if (billFilters.billing_id) params.billing_id = billFilters.billing_id;
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/bills`, { params });
      setBills(response.data.bills);
      setTotalPages(response.data.total_pages);
      setBillsDuplicateCount(response.data.duplicate_count || 0);
    } catch (error) {
      console.error("Failed to fetch bills:", error);
    }
  }, [botId, selectedLogin, page, search, billsHideDuplicates, billFilters]);

  const fetchPayments = useCallback(async () => {
    if (!selectedLogin) return;
    try {
      const params = { page: paymentPage, limit: 20, hide_duplicates: paymentsHideDuplicates };
      if (paymentFilters.vendor) params.vendor = paymentFilters.vendor;
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/payments`, { params });
      setPayments(response.data.payments);
      setPaymentTotalPages(response.data.pages);
      setPaymentsDuplicateCount(response.data.duplicate_count || 0);
    } catch (error) {
      console.error("Failed to fetch payments:", error);
    }
  }, [botId, selectedLogin, paymentPage, paymentsHideDuplicates, paymentFilters]);

  const fetchReports = useCallback(async () => {
    if (!selectedLogin) return;
    try {
      const params = { page: reportPage, limit: 20, hide_duplicates: reportsHideDuplicates };
      if (reportFilters.report_name) params.report_name = reportFilters.report_name;
      if (reportFilters.status) params.status = reportFilters.status;
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/reports`, { params });
      setReports(response.data.reports || []);
      setReportTotalPages(response.data.pages || 1);
      setReportsDuplicateCount(response.data.duplicate_count || 0);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    }
  }, [botId, selectedLogin, reportPage, reportsHideDuplicates, reportFilters]);

  const fetchLocations = useCallback(async (parentId = null) => {
    if (!selectedLogin) return;
    try {
      const params = { page: locationPage, limit: 50 };
      if (parentId) params.parent_id = parentId;
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/locations`, { params });
      setLocations(response.data.locations || []);
      setLocationTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  }, [botId, selectedLogin, locationPage]);

  const fetchLocationAccounts = useCallback(async (locationId) => {
    if (!selectedLogin || !locationId) return;
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/locations/${locationId}/accounts`);
      setLocationAccounts(response.data.accounts || []);
    } catch (error) {
      console.error("Failed to fetch location accounts:", error);
    }
  }, [botId, selectedLogin]);

  const fetchStats = async () => {
    if (!selectedLogin) return;
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchScraperStatus = async () => {
    if (!selectedLogin) return null;
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/scraper/status`);
      setScraperStatus(response.data);
      
      // Pre-populate start page inputs with last page when scraper is stopped/idle
      if (response.data.bills_status !== "running" && response.data.bills_last_page > 0) {
        setBillsStartPage(String(response.data.bills_last_page));
      }
      if (response.data.payments_status !== "running" && response.data.payments_last_page > 0) {
        setPaymentsStartPage(String(response.data.payments_last_page));
      }
      if (response.data.reports_status !== "running" && response.data.reports_last_page > 0) {
        setReportsStartPage(String(response.data.reports_last_page));
      }
      
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const fetchLogs = async () => {
    if (!selectedLogin) return;
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/scraper/logs`, { params: { limit: 200 } });
      setLogs(response.data.logs);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  };

  useEffect(() => {
    fetchBot();
    fetchLogins();
  }, [botId]);

  useEffect(() => {
    if (selectedLogin) {
      fetchBills();
      fetchPayments();
      fetchReports();
      fetchLocations();
      fetchStats();
      fetchScraperStatus();
      fetchLogs();

      // Poll only scraper status and logs - NOT logins list to prevent tab flickering
      const interval = setInterval(async () => {
        const status = await fetchScraperStatus();
        if (status?.is_running || status?.bills_status === "running" || status?.payments_status === "running" || status?.reports_status === "running" || status?.locations_status === "running") {
          fetchLogs();
          fetchStats();
          // Only update the bill_count for selected login without re-fetching all logins
        }
      }, 3000); // Reduced frequency from 2s to 3s

      return () => clearInterval(interval);
    }
  }, [selectedLogin, fetchBills, fetchPayments, fetchReports, fetchLocations]);

  useEffect(() => {
    if (selectedLogin) {
      setPage(1);
      setPaymentPage(1);
      setReportPage(1);
      setLocationPage(1);
      fetchBills();
      fetchPayments();
      fetchReports();
      fetchLocations();
    }
  }, [selectedLogin]);

  useEffect(() => {
    fetchBills();
  }, [page, search, fetchBills]);

  useEffect(() => {
    fetchPayments();
  }, [paymentPage, fetchPayments]);

  useEffect(() => {
    fetchReports();
  }, [reportPage, fetchReports]);

  useEffect(() => {
    fetchLocations();
  }, [locationPage, fetchLocations]);

  // Refetch data when table-specific duplicate settings or filters change
  useEffect(() => {
    if (selectedLogin) {
      fetchBills();
    }
  }, [billsHideDuplicates, billFilters]);

  useEffect(() => {
    if (selectedLogin) {
      fetchPayments();
    }
  }, [paymentsHideDuplicates, paymentFilters]);

  useEffect(() => {
    if (selectedLogin) {
      fetchReports();
    }
  }, [reportsHideDuplicates, reportFilters]);

  // Export functions
  const exportBillsCSV = async () => {
    if (!selectedLogin) return;
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/bills/export?hide_duplicates=${billsHideDuplicates}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bills_${selectedLogin.org_name || 'export'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Bills exported!");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const exportPaymentsCSV = async () => {
    if (!selectedLogin) return;
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/payments/export?hide_duplicates=${paymentsHideDuplicates}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments_${selectedLogin.org_name || 'export'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Payments exported!");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const exportReportsCSV = async () => {
    if (!selectedLogin) return;
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/reports/export?hide_duplicates=${reportsHideDuplicates}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reports_${selectedLogin.org_name || 'export'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Reports exported!");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // Delete functions
  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !selectedLogin) return;
    
    try {
      const { type, ids, all } = deleteTarget;
      let endpoint = `/bots/${botId}/logins/${selectedLogin.id}/${type}`;
      let params = all ? { delete_all: true } : { [`${type.slice(0, -1)}_ids`]: ids };
      
      await api.delete(endpoint, { data: params });
      toast.success(`Deleted ${all ? 'all' : ids.length} ${type}!`);
      
      // Clear selections and refresh
      if (type === 'bills') {
        setSelectedBillIds([]);
        fetchBills();
      } else if (type === 'payments') {
        setSelectedPaymentIds([]);
        fetchPayments();
      } else if (type === 'reports') {
        setSelectedReportIds([]);
        fetchReports();
      }
      fetchStats();
    } catch (error) {
      toast.error("Delete failed");
    }
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const addLogin = async () => {
    if (!newLogin.org_name || !newLogin.email || !newLogin.password) {
      toast.error("Agent name, email, and password are required");
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/bots/${botId}/logins`, newLogin);
      toast.success("Agent added!");
      setShowAddLogin(false);
      setNewLogin({ org_name: "", page_name: "", bills_url: "", payments_url: "", reports_url: "", locations_url: "", email: "", password: "" });
      fetchLogins();
    } catch (error) {
      toast.error("Failed to add agent");
    } finally {
      setLoading(false);
    }
  };

  const startEditLogin = (login) => {
    setEditingLogin({
      id: login.id,
      org_name: login.org_name || login.name || "",
      page_name: login.page_name || "",
      bills_url: login.bills_url || login.after_login_url || "",
      payments_url: login.payments_url || "",
      reports_url: login.reports_url || "",
      email: login.email || "",
      password: ""  // Don't pre-fill password for security
    });
    setShowEditLogin(true);
  };

  const saveEditLogin = async () => {
    if (!editingLogin) return;
    
    setLoading(true);
    try {
      // Only send fields that have values
      const updateData = {};
      if (editingLogin.org_name) updateData.org_name = editingLogin.org_name;
      if (editingLogin.page_name) updateData.page_name = editingLogin.page_name;
      if (editingLogin.bills_url) updateData.bills_url = editingLogin.bills_url;
      if (editingLogin.payments_url !== undefined) updateData.payments_url = editingLogin.payments_url;
      if (editingLogin.reports_url !== undefined) updateData.reports_url = editingLogin.reports_url;
      if (editingLogin.email) updateData.email = editingLogin.email;
      if (editingLogin.password) updateData.password = editingLogin.password;
      
      await api.put(`/bots/${botId}/logins/${editingLogin.id}`, updateData);
      toast.success("Agent updated!");
      setShowEditLogin(false);
      setEditingLogin(null);
      fetchLogins();
    } catch (error) {
      toast.error("Failed to update agent");
    } finally {
      setLoading(false);
    }
  };

  const deleteLogin = async (loginId, loginName) => {
    if (!window.confirm(`Delete "${loginName}" and all its scraped data?`)) return;
    
    try {
      await api.delete(`/bots/${botId}/logins/${loginId}`);
      toast.success("Agent deleted");
      if (selectedLogin?.id === loginId) {
        setSelectedLogin(null);
      }
      fetchLogins();
    } catch (error) {
      toast.error("Failed to delete agent");
    }
  };

  const startScraper = async (maxPages = null, resume = false, scrapeType = "all", startPage = null) => {
    if (!selectedLogin) return;
    
    try {
      setLoading(true);
      const payload = { 
        max_pages: maxPages, 
        resume,
        scrape_type: scrapeType
      };
      if (startPage && startPage > 0) {
        payload.start_page = startPage;
      }
      const response = await api.post(`/bots/${botId}/logins/${selectedLogin.id}/scraper/start`, payload);
      toast.success(response.data.message, { description: `Starting from page ${response.data.start_page}` });
      setShowLogs(true);
      await fetchScraperStatus();
    } catch (error) {
      toast.error("Failed to start scraper", { description: error.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  const stopScraper = async () => {
    if (!selectedLogin) return;
    
    try {
      await api.post(`/bots/${botId}/logins/${selectedLogin.id}/scraper/stop`);
      toast.info("Scraper stopping...");
      await fetchScraperStatus();
    } catch (error) {
      toast.error("Failed to stop scraper", { description: error.response?.data?.detail });
    }
  };

  const forceStopScraper = async () => {
    if (!selectedLogin) return;
    
    try {
      await api.post(`/bots/${botId}/logins/${selectedLogin.id}/scraper/force-stop`);
      toast.success("Scraper force stopped!");
      await fetchScraperStatus();
    } catch (error) {
      toast.error("Failed to force stop scraper", { description: error.response?.data?.detail });
    }
  };

  const downloadPaymentFile = async (filename) => {
    if (!filename || !selectedLogin) {
      toast.error("No file selected");
      return;
    }
    
    try {
      toast.info(`Downloading ${filename}...`);
      const response = await api.get(
        `/download/payments/${botId}/${selectedLogin.id}/${encodeURIComponent(filename)}`,
        { responseType: 'blob' }
      );
      
      // Try to get actual filename from headers
      let downloadFilename = filename;
      
      // Try X-Filename header first (our custom header)
      const xFilename = response.headers['x-filename'];
      if (xFilename) {
        downloadFilename = xFilename;
      } else {
        // Try Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
          if (match && match[1]) {
            downloadFilename = match[1];
          }
        }
      }
      
      // If still no extension, add based on content-type
      if (!downloadFilename.includes('.')) {
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('text/csv')) {
          downloadFilename += '.csv';
        } else if (contentType.includes('application/json')) {
          downloadFilename += '.json';
        } else if (contentType.includes('application/pdf')) {
          downloadFilename += '.pdf';
        } else {
          // Default to csv for payment files
          downloadFilename += '.csv';
        }
      }
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${downloadFilename}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed", { description: error.response?.data?.detail || error.message });
    }
  };

  const exportBills = async () => {
    if (!selectedLogin) return;
    
    try {
      const response = await api.get(`/bots/${botId}/logins/${selectedLogin.id}/bills/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bills_${selectedLogin.name.replace(/\s/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export downloaded!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const clearBills = async () => {
    if (!selectedLogin) return;
    if (!window.confirm("Delete all scraped bills for this login?")) return;
    
    try {
      const response = await api.delete(`/bots/${botId}/logins/${selectedLogin.id}/bills`);
      toast.success(`Deleted ${response.data.deleted_count} bills`);
      fetchBills();
      fetchStats();
      fetchLogins();
    } catch (error) {
      toast.error("Failed to clear bills");
    }
  };

  const progress = scraperStatus.total_pages > 0 
    ? (scraperStatus.current_page / scraperStatus.total_pages) * 100 
    : 0;

  if (!bot) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="text-zinc-500">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-zinc-400 hover:text-zinc-100 shrink-0 px-2">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Back</span>
              </Button>
              <div className="hidden md:block w-px h-6 bg-zinc-800"></div>
              <div className="min-w-0">
                <h1 className="text-base md:text-xl font-bold tracking-tight truncate" data-testid="bot-title">{bot.name}</h1>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-zinc-500 truncate hidden sm:block">{bot.portal_url}</p>
                  {bot.org && (
                    <Badge variant="outline" className="bg-blue-950/30 text-blue-400 border-blue-900/50 text-xs shrink-0">
                      <Building className="w-3 h-3 mr-1" />
                      {bot.org}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              {/* Mobile sidebar toggle */}
              <Sheet open={showMobileSidebar} onOpenChange={setShowMobileSidebar}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="md:hidden bg-zinc-800 border-zinc-700">
                    <Menu className="w-4 h-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] bg-zinc-900 border-zinc-800 p-4">
                  <SheetHeader className="mb-4">
                    <SheetTitle className="text-zinc-100">Agents</SheetTitle>
                  </SheetHeader>
                  <AgentsSidebar 
                    logins={logins}
                    selectedLogin={selectedLogin}
                    setSelectedLogin={setSelectedLogin}
                    onAddLogin={() => { setShowMobileSidebar(false); setShowAddLogin(true); }}
                    onEditLogin={(login) => { setShowMobileSidebar(false); startEditLogin(login); }}
                    onDeleteLogin={deleteLogin}
                    onClose={() => setShowMobileSidebar(false)}
                    onSelectLogin={async (login) => {
                      // Immediately fetch scraper status for this login
                      try {
                        const response = await api.get(`/bots/${botId}/logins/${login.id}/scraper/status`);
                        setScraperStatus(response.data);
                      } catch (e) {
                        console.error("Failed to fetch scraper status:", e);
                      }
                    }}
                  />
                </SheetContent>
              </Sheet>

              {selectedLogin && (
                <>
                  {scraperStatus.is_running && (
                    <>
                      <Button variant="destructive" size="sm" onClick={stopScraper} data-testid="stop-scraper-btn" className="bg-red-900/20 text-red-400 border border-red-900/50">
                        <Square className="w-4 h-4 md:mr-2" />
                        <span className="hidden md:inline">Stop</span>
                      </Button>
                      <Button variant="destructive" size="sm" onClick={forceStopScraper} data-testid="force-stop-btn" className="bg-red-600 text-white">
                        <XCircle className="w-4 h-4 md:mr-2" />
                        <span className="hidden md:inline">Force Stop</span>
                      </Button>
                    </>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowLogs(true)} data-testid="show-logs-btn" className="bg-zinc-800 border-zinc-700">
                    <Terminal className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-4 md:py-6">
        <div className="flex gap-6">
          {/* Desktop Sidebar - Hidden on mobile */}
          <div className="hidden md:block w-72 shrink-0">
            <AgentsSidebar 
              logins={logins}
              selectedLogin={selectedLogin}
              setSelectedLogin={setSelectedLogin}
              onAddLogin={() => setShowAddLogin(true)}
              onEditLogin={startEditLogin}
              onDeleteLogin={deleteLogin}
              onSelectLogin={async (login) => {
                // Immediately fetch scraper status for this login
                try {
                  const response = await api.get(`/bots/${botId}/logins/${login.id}/scraper/status`);
                  setScraperStatus(response.data);
                } catch (e) {
                  console.error("Failed to fetch scraper status:", e);
                }
              }}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile: Show selected login info */}
            {selectedLogin && (
              <div className="md:hidden mb-4">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{selectedLogin.name}</p>
                      <p className="text-xs text-zinc-500 truncate">{selectedLogin.email}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowMobileSidebar(true)} className="bg-zinc-800 border-zinc-700 shrink-0">
                      Switch
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {selectedLogin ? (
              <>
                {/* Server Info Banner - Shows when scraper is running */}
                {scraperStatus.is_running && scraperStatus.running_on_server && (
                  <div className={`mb-3 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${
                    scraperStatus.playwright_running_locally 
                      ? "bg-emerald-950/30 border border-emerald-900/50 text-emerald-400"
                      : "bg-amber-950/30 border border-amber-900/50 text-amber-400"
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${scraperStatus.playwright_running_locally ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`}></div>
                    <span>
                      {scraperStatus.playwright_running_locally 
                        ? `Playwright running on THIS server (${scraperStatus.this_server?.substring(0, 15)}...)`
                        : `Scraper started on: ${scraperStatus.running_on_server?.substring(0, 20)}... (different server)`
                      }
                    </span>
                    {!scraperStatus.playwright_running_locally && (
                      <span className="text-zinc-500 ml-auto">Workers may be stale if that server is offline</span>
                    )}
                  </div>
                )}

                {/* Separate Status Cards for Bills, Payments, Reports, and Locations */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 md:mb-6">
                  {/* Bills Status Card */}
                  <Card className={`bg-zinc-900 border-zinc-800 ${
                    scraperStatus.bills_status === "running" ? "border-blue-500/50" :
                    scraperStatus.bills_status === "completed" ? "border-emerald-500/50" :
                    scraperStatus.bills_status === "stopped" ? "border-amber-500/50" : ""
                  }`} data-testid="bills-status-card">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium">Bills</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${
                            scraperStatus.bills_status === "running" ? "bg-blue-950/30 text-blue-400 border-blue-900/50 animate-pulse" :
                            scraperStatus.bills_status === "completed" ? "bg-emerald-950/30 text-emerald-400 border-emerald-900/50" :
                            scraperStatus.bills_status === "stopped" ? "bg-amber-950/30 text-amber-400 border-amber-900/50" :
                            "bg-zinc-800 text-zinc-400 border-zinc-700"
                          }`}>
                            {scraperStatus.bills_status === "running" ? "Running" : 
                             scraperStatus.bills_status === "completed" ? "Done" :
                             scraperStatus.bills_status === "stopped" ? "Paused" : "Idle"}
                          </Badge>
                          {scraperStatus.bills_status !== "running" && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <MoreVertical className="w-4 h-4 text-zinc-500" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                                <div className="px-2 py-2">
                                  <Label className="text-xs text-zinc-400">Start from page</Label>
                                  <Input 
                                    type="number" 
                                    placeholder="1" 
                                    value={billsStartPage}
                                    onChange={(e) => setBillsStartPage(e.target.value)}
                                    className="h-7 w-full text-xs bg-zinc-800 border-zinc-700 mt-1"
                                    min="1"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Section - Always visible */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-400">
                            {scraperStatus.bills_status_message || 
                              (scraperStatus.bills_status === "running" 
                                ? `Page ${scraperStatus.bills_current_page || 0}/${scraperStatus.bills_total_pages || "?"}` 
                                : `${(billsHideDuplicates ? stats.unique_bills : stats.total_bills || 0).toLocaleString()} bills`)}
                          </span>
                          {stats.duplicate_bills > 0 && !billsHideDuplicates && (
                            <span className="text-amber-400 text-xs">+{stats.duplicate_bills} dupes</span>
                          )}
                        </div>
                        <Progress 
                          value={scraperStatus.bills_status === "running" && scraperStatus.bills_total_pages > 0 
                            ? (scraperStatus.bills_current_page / scraperStatus.bills_total_pages) * 100 
                            : 0} 
                          className="h-2 bg-zinc-800" 
                        />
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        {scraperStatus.bills_status === "running" ? (
                          <Button variant="outline" size="sm" onClick={stopScraper} className="flex-1 bg-red-900/20 text-red-400 border-red-900/50 h-8" data-testid="stop-bills-btn">
                            <Square className="w-3 h-3 mr-1" />
                            Pause
                          </Button>
                        ) : scraperStatus.bills_can_resume && scraperStatus.bills_last_page > 0 ? (
                          <>
                            <Button size="sm" onClick={() => startScraper(null, false, "bills", scraperStatus.bills_last_page)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-amber-600 hover:bg-amber-500 h-8" data-testid="resume-bills-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Resume p{scraperStatus.bills_last_page}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => startScraper(null, false, "bills", 1)} disabled={loading || scraperStatus.is_running} className="bg-zinc-800 border-zinc-700 h-8 px-2" data-testid="restart-bills-btn">
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="outline" size="sm" onClick={() => startScraper(5, false, "bills", parseInt(billsStartPage) || null)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-zinc-800 border-zinc-700 h-8" data-testid="test-bills-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Test
                            </Button>
                            <Button size="sm" onClick={() => startScraper(null, false, "bills", parseInt(billsStartPage) || null)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-blue-600 hover:bg-blue-500 h-8" data-testid="start-bills-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Start
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payments Status Card */}
                  <Card className={`bg-zinc-900 border-zinc-800 ${
                    scraperStatus.payments_status === "running" ? "border-purple-500/50" :
                    scraperStatus.payments_status === "completed" ? "border-emerald-500/50" :
                    scraperStatus.payments_status === "stopped" ? "border-amber-500/50" : ""
                  }`} data-testid="payments-status-card">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-purple-400" />
                          <span className="text-sm font-medium">Payments</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${
                            scraperStatus.payments_status === "running" ? "bg-purple-950/30 text-purple-400 border-purple-900/50 animate-pulse" :
                            scraperStatus.payments_status === "completed" ? "bg-emerald-950/30 text-emerald-400 border-emerald-900/50" :
                            scraperStatus.payments_status === "stopped" ? "bg-amber-950/30 text-amber-400 border-amber-900/50" :
                            "bg-zinc-800 text-zinc-400 border-zinc-700"
                          }`}>
                            {scraperStatus.payments_status === "running" ? "Running" : 
                             scraperStatus.payments_status === "completed" ? "Done" :
                             scraperStatus.payments_status === "stopped" ? "Paused" : "Idle"}
                          </Badge>
                          {scraperStatus.payments_status !== "running" && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <MoreVertical className="w-4 h-4 text-zinc-500" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                                <div className="px-2 py-2">
                                  <Label className="text-xs text-zinc-400">Start from page</Label>
                                  <Input 
                                    type="number" 
                                    placeholder="1" 
                                    value={paymentsStartPage}
                                    onChange={(e) => setPaymentsStartPage(e.target.value)}
                                    className="h-7 w-full text-xs bg-zinc-800 border-zinc-700 mt-1"
                                    min="1"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-400">
                            {scraperStatus.payments_status_message || 
                              (scraperStatus.payments_status === "running" 
                                ? `Page ${scraperStatus.payments_current_page || 0}/${scraperStatus.payments_total_pages || "?"}` 
                                : `${(paymentsHideDuplicates ? stats.unique_payments : stats.total_payments || 0).toLocaleString()} payments`)}
                          </span>
                          {stats.duplicate_payments > 0 && !paymentsHideDuplicates && (
                            <span className="text-amber-400 text-xs">+{stats.duplicate_payments} dupes</span>
                          )}
                        </div>
                        <Progress 
                          value={scraperStatus.payments_status === "running" && scraperStatus.payments_total_pages > 0 
                            ? (scraperStatus.payments_current_page / scraperStatus.payments_total_pages) * 100 
                            : 0} 
                          className="h-2 bg-zinc-800" 
                        />
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        {scraperStatus.payments_status === "running" ? (
                          <Button variant="outline" size="sm" onClick={stopScraper} className="flex-1 bg-red-900/20 text-red-400 border-red-900/50 h-8" data-testid="stop-payments-btn">
                            <Square className="w-3 h-3 mr-1" />
                            Pause
                          </Button>
                        ) : scraperStatus.payments_can_resume && scraperStatus.payments_last_page > 0 ? (
                          <>
                            <Button size="sm" onClick={() => startScraper(null, false, "payments", scraperStatus.payments_last_page)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-amber-600 hover:bg-amber-500 h-8" data-testid="resume-payments-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Resume p{scraperStatus.payments_last_page}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => startScraper(null, false, "payments", 1)} disabled={loading || scraperStatus.is_running} className="bg-zinc-800 border-zinc-700 h-8 px-2" data-testid="restart-payments-btn">
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="outline" size="sm" onClick={() => startScraper(5, false, "payments", parseInt(paymentsStartPage) || null)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-zinc-800 border-zinc-700 h-8" data-testid="test-payments-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Test
                            </Button>
                            <Button size="sm" onClick={() => startScraper(null, false, "payments", parseInt(paymentsStartPage) || null)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-purple-600 hover:bg-purple-500 h-8" data-testid="start-payments-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Start
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Reports Status Card */}
                  <Card className={`bg-zinc-900 border-zinc-800 ${
                    scraperStatus.reports_status === "running" ? "border-orange-500/50" :
                    scraperStatus.reports_status === "completed" ? "border-emerald-500/50" :
                    scraperStatus.reports_status === "stopped" ? "border-amber-500/50" : ""
                  }`} data-testid="reports-status-card">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-orange-400" />
                          <span className="text-sm font-medium">Reports</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${
                            scraperStatus.reports_status === "running" ? "bg-orange-950/30 text-orange-400 border-orange-900/50 animate-pulse" :
                            scraperStatus.reports_status === "completed" ? "bg-emerald-950/30 text-emerald-400 border-emerald-900/50" :
                            scraperStatus.reports_status === "stopped" ? "bg-amber-950/30 text-amber-400 border-amber-900/50" :
                            "bg-zinc-800 text-zinc-400 border-zinc-700"
                          }`}>
                            {scraperStatus.reports_status === "running" ? "Running" : 
                             scraperStatus.reports_status === "completed" ? "Done" :
                             scraperStatus.reports_status === "stopped" ? "Paused" : "Idle"}
                          </Badge>
                          {scraperStatus.reports_status !== "running" && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <MoreVertical className="w-4 h-4 text-zinc-500" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                                <div className="px-2 py-2">
                                  <Label className="text-xs text-zinc-400">Start from page</Label>
                                  <Input 
                                    type="number" 
                                    placeholder="1" 
                                    value={reportsStartPage}
                                    onChange={(e) => setReportsStartPage(e.target.value)}
                                    className="h-7 w-full text-xs bg-zinc-800 border-zinc-700 mt-1"
                                    min="1"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-400">
                            {scraperStatus.reports_status_message || 
                              (scraperStatus.reports_status === "running" 
                                ? `Page ${scraperStatus.reports_current_page || 0}/${scraperStatus.reports_total_pages || "?"}` 
                                : `${(reportsHideDuplicates ? stats.unique_reports : stats.total_reports || 0).toLocaleString()} reports`)}
                          </span>
                          {stats.duplicate_reports > 0 && !reportsHideDuplicates && (
                            <span className="text-amber-400 text-xs">+{stats.duplicate_reports} dupes</span>
                          )}
                        </div>
                        <Progress 
                          value={scraperStatus.reports_status === "running" && scraperStatus.reports_total_pages > 0 
                            ? (scraperStatus.reports_current_page / scraperStatus.reports_total_pages) * 100 
                            : 0} 
                          className="h-2 bg-zinc-800" 
                        />
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        {scraperStatus.reports_status === "running" ? (
                          <Button variant="outline" size="sm" onClick={stopScraper} className="flex-1 bg-red-900/20 text-red-400 border-red-900/50 h-8" data-testid="stop-reports-btn">
                            <Square className="w-3 h-3 mr-1" />
                            Pause
                          </Button>
                        ) : scraperStatus.reports_can_resume && scraperStatus.reports_last_page > 0 ? (
                          <>
                            <Button size="sm" onClick={() => startScraper(null, false, "reports", scraperStatus.reports_last_page)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-amber-600 hover:bg-amber-500 h-8" data-testid="resume-reports-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Resume p{scraperStatus.reports_last_page}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => startScraper(null, false, "reports", 1)} disabled={loading || scraperStatus.is_running} className="bg-zinc-800 border-zinc-700 h-8 px-2" data-testid="restart-reports-btn">
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="outline" size="sm" onClick={() => startScraper(5, false, "reports", parseInt(reportsStartPage) || null)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-zinc-800 border-zinc-700 h-8" data-testid="test-reports-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Test
                            </Button>
                            <Button size="sm" onClick={() => startScraper(null, false, "reports", parseInt(reportsStartPage) || null)} disabled={loading || scraperStatus.is_running} className="flex-1 bg-orange-600 hover:bg-orange-500 h-8" data-testid="start-reports-btn">
                              <Play className="w-3 h-3 mr-1" />
                              Start
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Locations Status Card */}
                  <Card className={`bg-zinc-900 border-zinc-800 ${
                    scraperStatus.locations_status === "running" ? "border-teal-500/50" :
                    scraperStatus.locations_status === "completed" ? "border-emerald-500/50" :
                    scraperStatus.locations_status === "stopped" ? "border-amber-500/50" : ""
                  }`} data-testid="locations-status-card">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-teal-400" />
                          <span className="text-sm font-medium">Locations</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${
                            scraperStatus.locations_status === "running" ? "bg-teal-950/30 text-teal-400 border-teal-900/50 animate-pulse" :
                            scraperStatus.locations_status === "completed" ? "bg-emerald-950/30 text-emerald-400 border-emerald-900/50" :
                            scraperStatus.locations_status === "stopped" ? "bg-amber-950/30 text-amber-400 border-amber-900/50" :
                            "bg-zinc-800 text-zinc-400 border-zinc-700"
                          }`}>
                            {scraperStatus.locations_status === "running" ? "Running" : 
                             scraperStatus.locations_status === "completed" ? "Done" :
                             scraperStatus.locations_status === "stopped" ? "Paused" : "Idle"}
                          </Badge>
                          {scraperStatus.locations_running_on_server && scraperStatus.locations_running_on_server !== scraperStatus.this_server && (
                            <span className="text-xs text-zinc-500" title={`Running on ${scraperStatus.locations_running_on_server}`}>
                              <Globe className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-400">
                            {scraperStatus.locations_status === "running" 
                              ? `${scraperStatus.locations_current_count || 0} locations` 
                              : `${(scraperStatus.locations_scraped_count || stats.total_locations || 0).toLocaleString()} locations`}
                          </span>
                          {scraperStatus.locations_status === "running" && (
                            <span className="text-teal-400 font-medium">{scraperStatus.locations_current_count?.toLocaleString() || 0} found</span>
                          )}
                        </div>
                        <Progress 
                          value={scraperStatus.locations_status === "running" ? 50 : 0} 
                          className="h-2 bg-zinc-800" 
                        />
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        {scraperStatus.locations_status === "running" ? (
                          <Button variant="outline" size="sm" onClick={stopScraper} className="flex-1 bg-red-900/20 text-red-400 border-red-900/50 h-8" data-testid="stop-locations-btn">
                            <Square className="w-3 h-3 mr-1" />
                            Stop
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => startScraper(null, false, "locations")} disabled={loading || scraperStatus.is_running || !selectedLogin?.locations_url} className="flex-1 bg-teal-600 hover:bg-teal-500 h-8" data-testid="start-locations-btn">
                            <Play className="w-3 h-3 mr-1" />
                            {selectedLogin?.locations_url ? "Start" : "No URL"}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Stats Grid - Responsive */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-3 md:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-zinc-500 uppercase">Bills</p>
                          <p className="text-lg md:text-2xl font-mono font-bold">{stats.total_bills?.toLocaleString() || 0}</p>
                        </div>
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-md bg-zinc-800 flex items-center justify-center">
                          <FileText className="w-4 h-4 md:w-5 md:h-5 text-zinc-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-3 md:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-zinc-500 uppercase">Payments</p>
                          <p className="text-lg md:text-2xl font-mono font-bold text-purple-400">{stats.total_payments?.toLocaleString() || 0}</p>
                        </div>
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-md bg-purple-950/30 flex items-center justify-center">
                          <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-purple-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-3 md:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-zinc-500 uppercase">S3 Files</p>
                          <p className="text-lg md:text-2xl font-mono font-bold text-emerald-400">{(stats.bills_with_s3 || 0) + (stats.payments_with_s3 || 0)}</p>
                        </div>
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-md bg-emerald-950/30 flex items-center justify-center">
                          <Download className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-3 md:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-zinc-500 uppercase">Top Vendor</p>
                          <p className="text-sm md:text-lg font-medium truncate max-w-[80px] md:max-w-[140px]">{stats.top_vendors?.[0]?._id || "N/A"}</p>
                        </div>
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-md bg-zinc-800 flex items-center justify-center">
                          <Building2 className="w-4 h-4 md:w-5 md:h-5 text-zinc-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Bills/Payments/Reports/Locations Tabs */}
                <div className="flex items-center gap-2 mb-4 bg-zinc-900 p-1 rounded-lg w-fit border border-zinc-800 overflow-x-auto">
                  <Button
                    variant={activeDataTab === "bills" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveDataTab("bills")}
                    className={activeDataTab === "bills" ? "bg-purple-600 text-white font-semibold" : "text-zinc-400 hover:text-white"}
                    data-testid="tab-bills"
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Bills ({(billsHideDuplicates ? stats.unique_bills : stats.total_bills)?.toLocaleString() || 0})
                  </Button>
                  <Button
                    variant={activeDataTab === "payments" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveDataTab("payments")}
                    className={activeDataTab === "payments" ? "bg-purple-600 text-white font-semibold" : "text-zinc-400 hover:text-white"}
                    data-testid="tab-payments"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payments ({(paymentsHideDuplicates ? stats.unique_payments : stats.total_payments)?.toLocaleString() || 0})
                  </Button>
                  <Button
                    variant={activeDataTab === "reports" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveDataTab("reports")}
                    className={activeDataTab === "reports" ? "bg-purple-600 text-white font-semibold" : "text-zinc-400 hover:text-white"}
                    data-testid="tab-reports"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Reports ({(reportsHideDuplicates ? stats.unique_reports : stats.total_reports)?.toLocaleString() || 0})
                  </Button>
                  <Button
                    variant={activeDataTab === "locations" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveDataTab("locations")}
                    className={activeDataTab === "locations" ? "bg-purple-600 text-white font-semibold" : "text-zinc-400 hover:text-white"}
                    data-testid="tab-locations"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Locations ({stats.total_locations?.toLocaleString() || 0})
                  </Button>
                </div>

                {/* Bills Table */}
                {activeDataTab === "bills" && (
                  <Card className="bg-zinc-900 border-zinc-800" data-testid="bills-table-card">
                    <CardHeader className="border-b border-zinc-800 py-2 md:py-3">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <CardTitle className="text-xs md:text-sm font-medium text-zinc-400">
                          Bills ({stats.total_bills?.toLocaleString() || 0})
                          {billsDuplicateCount > 0 && billsHideDuplicates && (
                            <span className="text-zinc-600 ml-2">({billsDuplicateCount} duplicates hidden)</span>
                          )}
                        </CardTitle>
                        
                        {/* Per-table controls - Right aligned */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Filters */}
                          <Input
                            placeholder="Filter vendor..."
                            value={billFilters.vendor}
                            onChange={(e) => { setBillFilters({...billFilters, vendor: e.target.value}); setPage(1); }}
                            className="w-32 h-7 text-xs bg-zinc-800 border-zinc-700"
                          />
                          <Input
                            placeholder="Filter billing ID..."
                            value={billFilters.billing_id}
                            onChange={(e) => { setBillFilters({...billFilters, billing_id: e.target.value}); setPage(1); }}
                            className="w-32 h-7 text-xs bg-zinc-800 border-zinc-700"
                          />
                          
                          {/* Show/Hide Duplicates */}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setBillsHideDuplicates(!billsHideDuplicates)}
                            className={`h-7 text-xs ${billsHideDuplicates ? 'bg-zinc-800 border-zinc-700' : 'bg-amber-900/30 text-amber-400 border-amber-900/50'}`}
                          >
                            {billsHideDuplicates ? "Unique" : "All"}
                          </Button>
                          
                          {/* Export CSV */}
                          <Button variant="outline" size="sm" onClick={exportBillsCSV} className="h-7 text-xs bg-zinc-800 border-zinc-700">
                            <Download className="w-3 h-3 mr-1" />
                            CSV
                          </Button>
                          
                          {/* Delete Selected/All */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setDeleteTarget({ type: 'bills', ids: selectedBillIds, all: selectedBillIds.length === 0 });
                              setShowDeleteConfirm(true);
                            }}
                            disabled={stats.total_bills === 0}
                            className="h-7 text-xs bg-red-900/20 text-red-400 border-red-900/50"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            {selectedBillIds.length > 0 ? `Delete (${selectedBillIds.length})` : "Clear All"}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-zinc-900/50 border-b border-zinc-800">
                              <TableHead className="w-8 py-2">
                                <input 
                                  type="checkbox" 
                                  checked={selectedBillIds.length > 0 && selectedBillIds.length === bills.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedBillIds(bills.map(b => b.id));
                                    } else {
                                      setSelectedBillIds([]);
                                    }
                                  }}
                                  className="rounded border-zinc-600 bg-zinc-800"
                                />
                              </TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Vendor</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Invoice Date</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Billing ID</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Start Date</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">End Date</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 text-right whitespace-nowrap">Charges</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 text-right whitespace-nowrap">Amount Due</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 text-right whitespace-nowrap">Pay Amount</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Payment Date</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 text-center whitespace-nowrap">PDF</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bills.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={11} className="text-center py-8 md:py-12 text-zinc-500">
                                  <FileText className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 opacity-20" />
                                  <p className="text-sm">No bills scraped yet</p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              bills.map((bill, idx) => (
                                <TableRow key={bill.id || idx} className={`border-b border-zinc-800/50 hover:bg-zinc-900/50 ${selectedBillIds.includes(bill.id) ? 'bg-blue-950/20' : ''}`}>
                                  <TableCell className="py-2">
                                    <input 
                                      type="checkbox"
                                      checked={selectedBillIds.includes(bill.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedBillIds([...selectedBillIds, bill.id]);
                                        } else {
                                          setSelectedBillIds(selectedBillIds.filter(id => id !== bill.id));
                                        }
                                      }}
                                      className="rounded border-zinc-600 bg-zinc-800"
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-300 py-2 max-w-[140px] truncate">{bill.vendor}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 whitespace-nowrap">{bill.invoice_date}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-300 py-2 whitespace-nowrap">{bill.billing_id}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 whitespace-nowrap">{bill.billing_start_date}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 whitespace-nowrap">{bill.billing_end_date}</TableCell>
                                  <TableCell className="font-mono text-xs text-emerald-400 py-2 text-right whitespace-nowrap">{bill.current_charges}</TableCell>
                                  <TableCell className="font-mono text-xs text-amber-400 py-2 text-right whitespace-nowrap">{bill.total_amount_due}</TableCell>
                                  <TableCell className="font-mono text-xs text-blue-400 py-2 text-right whitespace-nowrap">{bill.pay_amount}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 whitespace-nowrap">{bill.marked_for_payment}</TableCell>
                                  <TableCell className="py-2 text-center">
                                    {bill.s3_url ? (
                                      <a href={bill.s3_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded bg-emerald-950/30 text-emerald-400 hover:bg-emerald-900/40">
                                        <ExternalLink className="w-3 h-3 md:w-4 md:h-4" />
                                      </a>
                                    ) : (
                                      <span className="text-zinc-600">-</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between px-3 md:px-4 py-2 md:py-3 border-t border-zinc-800">
                          <span className="text-xs text-zinc-500">{page}/{totalPages}</span>
                          <div className="flex items-center gap-1 md:gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Payments Table */}
                {activeDataTab === "payments" && (
                  <Card className="bg-zinc-900 border-zinc-800" data-testid="payments-table-card">
                    <CardHeader className="border-b border-zinc-800 py-2 md:py-3">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <CardTitle className="text-xs md:text-sm font-medium text-zinc-400">
                          Payments ({stats.total_payments?.toLocaleString() || 0})
                          {paymentsDuplicateCount > 0 && paymentsHideDuplicates && (
                            <span className="text-zinc-600 ml-2">({paymentsDuplicateCount} duplicates hidden)</span>
                          )}
                        </CardTitle>
                        
                        {/* Per-table controls - Right aligned */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Filters */}
                          <Input
                            placeholder="Filter by vendor..."
                            value={paymentFilters.vendor}
                            onChange={(e) => { setPaymentFilters({...paymentFilters, vendor: e.target.value}); setPaymentPage(1); }}
                            className="w-32 h-7 text-xs bg-zinc-800 border-zinc-700"
                          />
                          
                          {/* Show/Hide Duplicates */}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setPaymentsHideDuplicates(!paymentsHideDuplicates)}
                            className={`h-7 text-xs ${paymentsHideDuplicates ? 'bg-zinc-800 border-zinc-700' : 'bg-amber-900/30 text-amber-400 border-amber-900/50'}`}
                          >
                            {paymentsHideDuplicates ? "Unique" : "All"}
                          </Button>
                          
                          {/* Export CSV */}
                          <Button variant="outline" size="sm" onClick={exportPaymentsCSV} className="h-7 text-xs bg-zinc-800 border-zinc-700">
                            <Download className="w-3 h-3 mr-1" />
                            CSV
                          </Button>
                          
                          {/* Delete Selected/All */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setDeleteTarget({ type: 'payments', ids: selectedPaymentIds, all: selectedPaymentIds.length === 0 });
                              setShowDeleteConfirm(true);
                            }}
                            disabled={stats.total_payments === 0}
                            className="h-7 text-xs bg-red-900/20 text-red-400 border-red-900/50"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            {selectedPaymentIds.length > 0 ? `Delete (${selectedPaymentIds.length})` : "Clear All"}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-zinc-900/50 border-b border-zinc-800">
                              <TableHead className="w-8 py-2">
                                <input 
                                  type="checkbox" 
                                  checked={selectedPaymentIds.length > 0 && selectedPaymentIds.length === payments.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPaymentIds(payments.map(p => p.id));
                                    } else {
                                      setSelectedPaymentIds([]);
                                    }
                                  }}
                                  className="rounded border-zinc-600 bg-zinc-800"
                                />
                              </TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Date</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Qty of Bills</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 text-right whitespace-nowrap">Amount Due</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">File Name</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 text-center whitespace-nowrap">Download</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {payments.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 md:py-12 text-zinc-500">
                                  <CreditCard className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 opacity-20" />
                                  <p className="text-sm">No payments scraped yet</p>
                                  <p className="text-xs text-zinc-600 mt-1">Configure a Payments URL in agent settings</p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              payments.map((payment, idx) => (
                                <TableRow key={payment.id || idx} className={`border-b border-zinc-800/50 hover:bg-zinc-900/50 ${selectedPaymentIds.includes(payment.id) ? 'bg-purple-950/20' : ''}`}>
                                  <TableCell className="py-2">
                                    <input 
                                      type="checkbox"
                                      checked={selectedPaymentIds.includes(payment.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedPaymentIds([...selectedPaymentIds, payment.id]);
                                        } else {
                                          setSelectedPaymentIds(selectedPaymentIds.filter(id => id !== payment.id));
                                        }
                                      }}
                                      className="rounded border-zinc-600 bg-zinc-800"
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-300 py-2 whitespace-nowrap">{payment.date}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 whitespace-nowrap">{payment.qty_of_bills}</TableCell>
                                  <TableCell className="font-mono text-xs text-purple-400 py-2 text-right whitespace-nowrap">{payment.amount_due}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 max-w-[200px]">
                                    {(payment.s3_url || payment.additional_files?.length > 0) ? (
                                      <span className="flex items-center gap-1">
                                        <span className="truncate">{payment.file_name || (payment.additional_files?.[0]?.file_name) || "Files"}</span>
                                        {payment.additional_files?.length > 0 && (
                                          <span className="text-purple-400 text-[10px]">+{payment.additional_files.length}</span>
                                        )}
                                      </span>
                                    ) : "-"}
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {(payment.s3_url || payment.additional_files?.length > 0) ? (
                                      <div className="flex items-center gap-1 justify-center">
                                        {/* Dropdown for multiple files */}
                                        {(payment.additional_files?.length > 0) ? (
                                          <select
                                            className="bg-zinc-800 border border-zinc-700 rounded text-xs py-1 px-2 text-zinc-300 max-w-[140px]"
                                            onChange={(e) => {
                                              if (e.target.value) {
                                                downloadPaymentFile(e.target.value);
                                                e.target.value = '';
                                              }
                                            }}
                                            defaultValue=""
                                            data-testid={`payment-download-${idx}`}
                                          >
                                            <option value="">Download ({(payment.additional_files?.length || 0) + (payment.s3_url ? 1 : 0)})</option>
                                            {payment.s3_url && payment.file_name && (
                                              <option value={payment.file_name}>
                                                {(payment.file_name || "Main File").substring(0, 30)}
                                              </option>
                                            )}
                                            {payment.additional_files.map((file, fIdx) => (
                                              <option key={fIdx} value={file.file_name || `file_${fIdx}`}>
                                                {(file.file_name || `File ${fIdx + 1}`).substring(0, 30)}
                                              </option>
                                            ))}
                                          </select>
                                        ) : payment.s3_url && payment.file_name ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 md:h-8 md:w-8 p-0 bg-purple-950/30 text-purple-400 hover:bg-purple-900/40"
                                            onClick={() => downloadPaymentFile(payment.file_name)}
                                          >
                                            <Download className="w-3 h-3 md:w-4 md:h-4" />
                                          </Button>
                                        ) : null}
                                      </div>
                                    ) : (
                                      <span className="text-zinc-600 text-center block">-</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {paymentTotalPages > 1 && (
                        <div className="flex items-center justify-between px-3 md:px-4 py-2 md:py-3 border-t border-zinc-800">
                          <span className="text-xs text-zinc-500">{paymentPage}/{paymentTotalPages}</span>
                          <div className="flex items-center gap-1 md:gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPaymentPage(p => Math.max(1, p - 1))} disabled={paymentPage === 1} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setPaymentPage(p => Math.min(paymentTotalPages, p + 1))} disabled={paymentPage === paymentTotalPages} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Reports Table */}
                {activeDataTab === "reports" && (
                  <Card className="bg-zinc-900 border-zinc-800" data-testid="reports-table-card">
                    <CardHeader className="border-b border-zinc-800 py-2 md:py-3">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <CardTitle className="text-xs md:text-sm font-medium text-zinc-400">
                          Reports ({stats.total_reports?.toLocaleString() || 0})
                          {reportsDuplicateCount > 0 && reportsHideDuplicates && (
                            <span className="text-zinc-600 ml-2">({reportsDuplicateCount} duplicates hidden)</span>
                          )}
                        </CardTitle>
                        
                        {/* Per-table controls - Right aligned */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Filters */}
                          <Input
                            placeholder="Filter report name..."
                            value={reportFilters.report_name}
                            onChange={(e) => { setReportFilters({...reportFilters, report_name: e.target.value}); setReportPage(1); }}
                            className="w-32 h-7 text-xs bg-zinc-800 border-zinc-700"
                          />
                          <Input
                            placeholder="Filter status..."
                            value={reportFilters.status}
                            onChange={(e) => { setReportFilters({...reportFilters, status: e.target.value}); setReportPage(1); }}
                            className="w-28 h-7 text-xs bg-zinc-800 border-zinc-700"
                          />
                          
                          {/* Show/Hide Duplicates */}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setReportsHideDuplicates(!reportsHideDuplicates)}
                            className={`h-7 text-xs ${reportsHideDuplicates ? 'bg-zinc-800 border-zinc-700' : 'bg-amber-900/30 text-amber-400 border-amber-900/50'}`}
                          >
                            {reportsHideDuplicates ? "Unique" : "All"}
                          </Button>
                          
                          {/* Export CSV */}
                          <Button variant="outline" size="sm" onClick={exportReportsCSV} className="h-7 text-xs bg-zinc-800 border-zinc-700">
                            <Download className="w-3 h-3 mr-1" />
                            CSV
                          </Button>
                          
                          {/* Delete Selected/All */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setDeleteTarget({ type: 'reports', ids: selectedReportIds, all: selectedReportIds.length === 0 });
                              setShowDeleteConfirm(true);
                            }}
                            disabled={stats.total_reports === 0}
                            className="h-7 text-xs bg-red-900/20 text-red-400 border-red-900/50"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            {selectedReportIds.length > 0 ? `Delete (${selectedReportIds.length})` : "Clear All"}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-zinc-900/50 border-b border-zinc-800">
                              <TableHead className="w-8 py-2">
                                <input 
                                  type="checkbox" 
                                  checked={selectedReportIds.length > 0 && selectedReportIds.length === reports.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedReportIds(reports.map(r => r.id));
                                    } else {
                                      setSelectedReportIds([]);
                                    }
                                  }}
                                  className="rounded border-zinc-600 bg-zinc-800"
                                />
                              </TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Report Name</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Date</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Status</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">File</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 text-center whitespace-nowrap">Download</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reports.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 md:py-12 text-zinc-500">
                                  <FileText className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 opacity-20" />
                                  <p className="text-sm">No reports scraped yet</p>
                                  <p className="text-xs text-zinc-600 mt-1">Configure a Reports URL in agent settings</p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              reports.map((report, idx) => (
                                <TableRow key={report.id || idx} className={`border-b border-zinc-800/50 hover:bg-zinc-900/50 ${selectedReportIds.includes(report.id) ? 'bg-orange-950/20' : ''}`}>
                                  <TableCell className="py-2">
                                    <input 
                                      type="checkbox"
                                      checked={selectedReportIds.includes(report.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedReportIds([...selectedReportIds, report.id]);
                                        } else {
                                          setSelectedReportIds(selectedReportIds.filter(id => id !== report.id));
                                        }
                                      }}
                                      className="rounded border-zinc-600 bg-zinc-800"
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-300 py-2 max-w-[200px] truncate">{report.report_name}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 whitespace-nowrap">{report.report_date}</TableCell>
                                  <TableCell className="py-2">
                                    <Badge variant="outline" className={`text-xs ${
                                      report.status?.toLowerCase() === 'completed' ? 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50' :
                                      report.status?.toLowerCase() === 'pending' ? 'bg-amber-950/30 text-amber-400 border-amber-900/50' :
                                      'bg-zinc-800 text-zinc-400 border-zinc-700'
                                    }`}>
                                      {report.status || 'Unknown'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 max-w-[200px] truncate">{report.file_name || "-"}</TableCell>
                                  <TableCell className="py-2 text-center">
                                    {report.s3_url ? (
                                      <a href={report.s3_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded bg-orange-950/30 text-orange-400 hover:bg-orange-900/40">
                                        <ExternalLink className="w-3 h-3 md:w-4 md:h-4" />
                                      </a>
                                    ) : (
                                      <span className="text-zinc-600">-</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {reportTotalPages > 1 && (
                        <div className="flex items-center justify-between px-3 md:px-4 py-2 md:py-3 border-t border-zinc-800">
                          <span className="text-xs text-zinc-500">{reportPage}/{reportTotalPages}</span>
                          <div className="flex items-center gap-1 md:gap-2">
                            <Button variant="outline" size="sm" onClick={() => setReportPage(p => Math.max(1, p - 1))} disabled={reportPage === 1} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setReportPage(p => Math.min(reportTotalPages, p + 1))} disabled={reportPage === reportTotalPages} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Locations Table */}
                {activeDataTab === "locations" && (
                  <Card className="bg-zinc-900 border-zinc-800" data-testid="locations-table-card">
                    <CardHeader className="border-b border-zinc-800 py-2 md:py-3">
                      <CardTitle className="text-xs md:text-sm font-medium text-zinc-400 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-teal-400" />
                        Locations Hierarchy ({stats.total_locations?.toLocaleString() || 0} locations, {stats.total_accounts?.toLocaleString() || 0} accounts)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-zinc-900/50 border-b border-zinc-800">
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Location Name</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Type</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Count</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Depth</TableHead>
                              <TableHead className="text-xs uppercase text-zinc-500 py-2 md:py-3 whitespace-nowrap">Children</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {locations.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center py-8 md:py-12 text-zinc-500">
                                  <MapPin className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 opacity-20" />
                                  <p className="text-sm">No locations scraped yet</p>
                                  <p className="text-xs text-zinc-600 mt-1">Configure a Locations URL in agent settings and run the Locations scraper</p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              locations.map((loc, idx) => (
                                <TableRow 
                                  key={loc.id || idx} 
                                  className={`border-b border-zinc-800/50 hover:bg-zinc-900/50 cursor-pointer ${selectedLocationId === loc.id ? 'bg-teal-950/20' : ''}`}
                                  onClick={() => {
                                    setSelectedLocationId(loc.id);
                                    if (loc.has_children) {
                                      fetchLocations(loc.id);
                                    } else {
                                      fetchLocationAccounts(loc.id);
                                    }
                                  }}
                                >
                                  <TableCell className="font-mono text-xs text-zinc-300 py-2 flex items-center gap-2">
                                    {loc.has_children && <ChevronDown className="w-3 h-3 text-zinc-500" />}
                                    {loc.name}
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <Badge variant="outline" className={`text-xs ${
                                      loc.type === 'property' ? 'bg-blue-950/30 text-blue-400 border-blue-900/50' :
                                      loc.type === 'location' ? 'bg-purple-950/30 text-purple-400 border-purple-900/50' :
                                      loc.type === 'common' ? 'bg-teal-950/30 text-teal-400 border-teal-900/50' :
                                      loc.type === 'units' ? 'bg-amber-950/30 text-amber-400 border-amber-900/50' :
                                      'bg-zinc-800 text-zinc-400 border-zinc-700'
                                    }`}>
                                      {loc.type}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2">{loc.count?.toLocaleString() || 0}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2">{loc.depth}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2">{loc.child_count || 0}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {/* Back to root button */}
                      {locations.length > 0 && locations[0]?.parent_id && (
                        <div className="px-3 md:px-4 py-2 border-t border-zinc-800">
                          <Button variant="outline" size="sm" onClick={() => fetchLocations(null)} className="bg-zinc-800 border-zinc-700">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Root
                          </Button>
                        </div>
                      )}
                      
                      {locationTotalPages > 1 && (
                        <div className="flex items-center justify-between px-3 md:px-4 py-2 md:py-3 border-t border-zinc-800">
                          <span className="text-xs text-zinc-500">{locationPage}/{locationTotalPages}</span>
                          <div className="flex items-center gap-1 md:gap-2">
                            <Button variant="outline" size="sm" onClick={() => setLocationPage(p => Math.max(1, p - 1))} disabled={locationPage === 1} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setLocationPage(p => Math.min(locationTotalPages, p + 1))} disabled={locationPage === locationTotalPages} className="bg-zinc-800 border-zinc-700 h-7 w-7 md:h-8 md:w-8 p-0">
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Location Accounts Section */}
                      {selectedLocationId && locationAccounts.length > 0 && (
                        <div className="border-t border-zinc-800 p-4">
                          <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-teal-400" />
                            Utility Accounts ({locationAccounts.length})
                          </h4>
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-zinc-900/50 border-b border-zinc-800">
                                <TableHead className="text-xs uppercase text-zinc-500 py-2">Vendor</TableHead>
                                <TableHead className="text-xs uppercase text-zinc-500 py-2">Account</TableHead>
                                <TableHead className="text-xs uppercase text-zinc-500 py-2">Utility</TableHead>
                                <TableHead className="text-xs uppercase text-zinc-500 py-2">Status</TableHead>
                                <TableHead className="text-xs uppercase text-zinc-500 py-2">Address</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {locationAccounts.map((acct, idx) => (
                                <TableRow key={acct.id || idx} className="border-b border-zinc-800/50">
                                  <TableCell className="font-mono text-xs text-zinc-300 py-2">{acct.vendor}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2">{acct.billing_account}</TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2">{acct.utility_type}</TableCell>
                                  <TableCell className="py-2">
                                    <Badge variant="outline" className={`text-xs ${
                                      acct.status === 'open' ? 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50' :
                                      'bg-zinc-800 text-zinc-400 border-zinc-700'
                                    }`}>
                                      {acct.status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-zinc-400 py-2 max-w-[200px] truncate">{acct.service_address}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
                <CardContent className="py-12 md:py-16 text-center">
                  <User className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-lg md:text-xl font-medium text-zinc-400 mb-2">Select a login</h3>
                  <p className="text-sm text-zinc-600 mb-4">Choose a login to start scraping</p>
                  <Button onClick={() => setShowAddLogin(true)} className="bg-white text-black hover:bg-zinc-200">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Login
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Logs Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-[95vw] md:max-w-3xl bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-100 text-sm md:text-base">
              <Terminal className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" />
              Logs - {selectedLogin?.org_name || selectedLogin?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 sr-only">
              Real-time scraper logs
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[50vh] md:h-[400px] rounded-md bg-black/80 p-3 md:p-4 border border-zinc-800">
            <div className="font-mono text-xs">
              {logs.length === 0 ? (
                <p className="text-zinc-600 text-center py-8">No logs yet</p>
              ) : (
                logs.map((log, idx) => (
                  <div key={idx} className="py-0.5 flex flex-wrap md:flex-nowrap">
                    <span className="text-zinc-600 shrink-0 mr-2">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                    <span className="text-green-400 break-all">{log.message}</span>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <div className="flex justify-between items-center mt-2">
            <Button variant="outline" size="sm" onClick={fetchLogs} className="bg-zinc-800 border-zinc-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Badge variant="outline" className={scraperStatus.is_running ? "bg-blue-950/30 text-blue-400 border-blue-900/50" : "bg-zinc-800 text-zinc-400 border-zinc-700"}>
              {scraperStatus.is_running ? "Running" : "Idle"}
            </Badge>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Agent Dialog */}
      <Dialog open={showAddLogin} onOpenChange={setShowAddLogin}>
        <DialogContent className="max-w-[95vw] md:max-w-md bg-zinc-900 border-zinc-800" data-testid="add-agent-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Add New Agent</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Configure agent credentials and scraping target
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Agent Name *</Label>
              <Input
                value={newLogin.org_name}
                onChange={(e) => setNewLogin({ ...newLogin, org_name: e.target.value })}
                placeholder="e.g., SNS Agent, Bayshore Agent"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="agent-name-input"
              />
              <p className="text-xs text-zinc-600 mt-1">Display name for this agent</p>
            </div>
            <div>
              <Label className="text-zinc-400">Portal Email *</Label>
              <Input
                value={newLogin.email}
                onChange={(e) => setNewLogin({ ...newLogin, email: e.target.value })}
                placeholder="user@example.com"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Portal Password *</Label>
              <Input
                type="password"
                value={newLogin.password}
                onChange={(e) => setNewLogin({ ...newLogin, password: e.target.value })}
                placeholder="Enter password"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="login-password-input"
              />
            </div>
            <div className="border-t border-zinc-800 pt-4">
              <Label className="text-zinc-400 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Page Name
              </Label>
              <Input
                value={newLogin.page_name}
                onChange={(e) => setNewLogin({ ...newLogin, page_name: e.target.value })}
                placeholder="e.g., Bills, Invoices"
                className="bg-zinc-800 border-zinc-700 mt-1"
              />
              <p className="text-xs text-zinc-600 mt-1">Page to scrape (displayed in sidebar)</p>
            </div>
            <div>
              <Label className="text-zinc-400 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Bills URL
              </Label>
              <Input
                value={newLogin.bills_url}
                onChange={(e) => setNewLogin({ ...newLogin, bills_url: e.target.value })}
                placeholder="e.g., /94092/bills/"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="bills-url-input"
              />
              <p className="text-xs text-zinc-600 mt-1">URL path for bills page</p>
            </div>
            <div>
              <Label className="text-zinc-400 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payments URL
              </Label>
              <Input
                value={newLogin.payments_url}
                onChange={(e) => setNewLogin({ ...newLogin, payments_url: e.target.value })}
                placeholder="e.g., /94092/payments/"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="payments-url-input"
              />
              <p className="text-xs text-zinc-600 mt-1">URL path for payments page (optional)</p>
            </div>
            <div>
              <Label className="text-zinc-400 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Reports URL
              </Label>
              <Input
                value={newLogin.reports_url}
                onChange={(e) => setNewLogin({ ...newLogin, reports_url: e.target.value })}
                placeholder="e.g., /94092/reports/"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="reports-url-input"
              />
              <p className="text-xs text-zinc-600 mt-1">URL path for reports page (optional)</p>
            </div>
            <div>
              <Label className="text-zinc-400 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Locations URL
              </Label>
              <Input
                value={newLogin.locations_url}
                onChange={(e) => setNewLogin({ ...newLogin, locations_url: e.target.value })}
                placeholder="e.g., /93296/locations/hierarchies/"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="locations-url-input"
              />
              <p className="text-xs text-zinc-600 mt-1">URL path for locations hierarchy page (optional)</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddLogin(false)} className="bg-zinc-800 border-zinc-700">Cancel</Button>
            <Button onClick={addLogin} disabled={loading} className="bg-white text-black hover:bg-zinc-200" data-testid="add-agent-submit">
              {loading ? "Adding..." : "Add Agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog open={showEditLogin} onOpenChange={setShowEditLogin}>
        <DialogContent className="max-w-[95vw] md:max-w-md bg-zinc-900 border-zinc-800" data-testid="edit-agent-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit Agent</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Update agent details and scraping configuration
            </DialogDescription>
          </DialogHeader>
          {editingLogin && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Organization Name</Label>
                <Input
                  value={editingLogin.org_name}
                  onChange={(e) => setEditingLogin({ ...editingLogin, org_name: e.target.value })}
                  placeholder="e.g., SNS, Company A"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-org-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Portal Email</Label>
                <Input
                  value={editingLogin.email}
                  onChange={(e) => setEditingLogin({ ...editingLogin, email: e.target.value })}
                  placeholder="user@example.com"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-email-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Portal Password</Label>
                <Input
                  type="password"
                  value={editingLogin.password}
                  onChange={(e) => setEditingLogin({ ...editingLogin, password: e.target.value })}
                  placeholder="Leave blank to keep current"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-password-input"
                />
                <p className="text-xs text-zinc-600 mt-1">Leave blank to keep existing password</p>
              </div>
              <div className="border-t border-zinc-800 pt-4">
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Page Name
                </Label>
                <Input
                  value={editingLogin.page_name}
                  onChange={(e) => setEditingLogin({ ...editingLogin, page_name: e.target.value })}
                  placeholder="e.g., Bills, Invoices"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
                <p className="text-xs text-zinc-600 mt-1">Page type displayed in sidebar</p>
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Bills URL
                </Label>
                <Input
                  value={editingLogin.bills_url}
                  onChange={(e) => setEditingLogin({ ...editingLogin, bills_url: e.target.value })}
                  placeholder="e.g., /94092/bills/"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-bills-url-input"
                />
                <p className="text-xs text-zinc-600 mt-1">URL path for bills page</p>
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Payments URL
                </Label>
                <Input
                  value={editingLogin.payments_url}
                  onChange={(e) => setEditingLogin({ ...editingLogin, payments_url: e.target.value })}
                  placeholder="e.g., /94092/payments/"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-payments-url-input"
                />
                <p className="text-xs text-zinc-600 mt-1">URL path for payments page (optional)</p>
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Reports URL
                </Label>
                <Input
                  value={editingLogin.reports_url}
                  onChange={(e) => setEditingLogin({ ...editingLogin, reports_url: e.target.value })}
                  placeholder="e.g., /94092/reports/"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-reports-url-input"
                />
                <p className="text-xs text-zinc-600 mt-1">URL path for reports page (optional)</p>
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Locations URL
                </Label>
                <Input
                  value={editingLogin.locations_url || ""}
                  onChange={(e) => setEditingLogin({ ...editingLogin, locations_url: e.target.value })}
                  placeholder="e.g., /93296/locations/hierarchies/"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-locations-url-input"
                />
                <p className="text-xs text-zinc-600 mt-1">URL path for locations hierarchy page (optional)</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditLogin(false)} className="bg-zinc-800 border-zinc-700">Cancel</Button>
            <Button onClick={saveEditLogin} disabled={loading} className="bg-white text-black hover:bg-zinc-200" data-testid="edit-login-submit">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {deleteTarget?.all 
                ? `Are you sure you want to delete ALL ${deleteTarget?.type}? This action cannot be undone.`
                : `Are you sure you want to delete ${deleteTarget?.ids?.length || 0} selected ${deleteTarget?.type}? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="bg-zinc-800 border-zinc-700">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-500">
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteTarget?.all ? "Delete All" : "Delete Selected"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
